import React, { useState, useEffect, } from "react";
import AceEditor from "react-ace";
import "ace-builds/src-noconflict/mode-json";
import "ace-builds/src-noconflict/theme-cobalt";

import styled from "styled-components";
import {
  Button,
  Input,
  FormGroup,
  Label,
} from 'reactstrap';
import {
  put,
  endpoints,
} from "utils/axios";
import { withSnackbarConsumer, Color } from "../../contexts/SnackbarContext/SnackbarContext.jsx";
import Modal from "./Modal.jsx";

export const ModalHeader = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
`;

export const Title = styled.p`
  font-size: 20px;
  font-weight: 500;
  color: #2E384D;
`;

function validateJsonAndGetAnnotations(value) {
  try {
    JSON.parse(value);
    return [];
  } catch (err) {
    const message = err.message || "Invalid JSON";
    const match = message.match(/position (\d+)/i);
    let row = 0;

    if (match) {
      const pos = Number(match[1]);
      row = value.slice(0, pos).split("\n").length - 1;
    }

    return [
      {
        row,
        column: 0,
        type: "error",
        text: message,
      },
    ];
  }
}

const JsonEditor = ({ value, onChange, onAnnotationsChange }) => {
  const editorRef = React.useRef(null);

  useEffect(() => {
    if (!editorRef.current) return;

    const annotations = validateJsonAndGetAnnotations(value);
    const session = editorRef.current.editor.getSession();

    session.setAnnotations(annotations);
    onAnnotationsChange(annotations);
  }, [value]);

  return (
    <AceEditor
      mode="json"
      theme="cobalt"
      value={value}
      onChange={onChange}
      width="100%"
      height="100%"
      fontSize={14}
      showPrintMargin={false}
      showGutter
      highlightActiveLine
      ref={editorRef}
      setOptions={{
        useWorker: false,
        showLineNumbers: true,
        tabSize: 2,
      }}
    />
  );
};

const Section = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const SectionTitle = styled.h5`
  margin: 0;
  font-size: 14px;
  font-weight: 500;
`;

const ValueSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  height: 100%;
`;

function ConfigModal({
  title,
  isOpen,
  setIsOpen,
  config,
  setSelectedConfig,
  snackbarContext,
}) {
  const [rawValue, setRawValue] = useState("");
  const [jsonError, setJsonError] = useState(false);
  const isJsonLike = config.type === "JSON" || config.type === "list";

  const onAnnotationsChange = (notes) => {
    setJsonError(notes.some(a => a.type === "error"));
  };

  useEffect(() => {
    if (!config) return;

    if (config.type === "JSON" || config.type === "list") {
      setRawValue(JSON.stringify(config.value, null, 2));
    } else {
      setRawValue(config.value);
    }
  }, [config]);

  const handleUpdate = async () => {
    if (jsonError) {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        "JSON inválido. Corrija antes de salvar."
      );
      return;
    }
    try {
      await put(
        endpoints.configs.update(config.id),
        { ...config, value: rawValue }
      );

      snackbarContext.setNotificationTimeOut(
        Color.success,
        "Configuração atualizada."
      );
    } catch (error) {
      console.error(error);

      snackbarContext.setNotificationTimeOut(
        Color.danger,
        "Erro ao atualizar configuração."
      );
    } finally {
      setIsOpen(false);
      setSelectedConfig(null);
    }
  };

  const validateJson = () => {
    try {
      JSON.parse(rawValue);
      snackbarContext.setNotificationTimeOut(
        Color.success,
        "JSON válido"
      );
    } catch (e) {
      snackbarContext.setNotificationTimeOut(
        Color.danger,
        "JSON inválido"
      );
    }
  };

  const handleCancel = () => {
    setIsOpen(false);
    setSelectedConfig(null);
  };

  const renderValueInput = () => {
    switch (config.type) {
      case "bool":
        return (
          <FormGroup check>
            <Label check>
              <Input
                type="checkbox"
                checked={Boolean(rawValue)}
                onChange={e => setRawValue(e.target.checked)}
              />
              <span className="form-check-sign" />
            </Label>
          </FormGroup>
        );

      case "int":
      case "float":
        return (
          <Input
            type="number"
            value={rawValue}
            onChange={e => setRawValue(e.target.value)}
          />
        );

      default:
        return (
          <Input
            type="text"
            value={rawValue}
            onChange={e => setRawValue(e.target.value)}
          />
        );
    }
  };

  if (!config) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleCancel}
      title={title}
      width={isJsonLike ? "60vw" : "50vw"}
      height={isJsonLike ? "90vh" : "40vh"}
      maxHeight="90vh"
      footer={(
        <React.Fragment>
          {isJsonLike && (
            <Button color="secondary" onClick={validateJson}>
              Validar JSON
            </Button>
          )}
          <Button
            color="info"
            disabled={jsonError}
            onClick={handleUpdate}
          >
            Salvar
          </Button>
        </React.Fragment>
      )}
    >
      <Section>
        <SectionTitle>Descrição</SectionTitle>
        <p>{config.description}</p>
      </Section>

      <ValueSection>
        <SectionTitle>Valor</SectionTitle>

        {isJsonLike ? (
          <JsonEditor
            value={rawValue}
            onChange={(value) => setRawValue(value)}
            onAnnotationsChange={onAnnotationsChange}
          />
        ) : (
          renderValueInput()
        )}
      </ValueSection>
    </Modal>
  );
}

export default withSnackbarConsumer(ConfigModal);
